import { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { X, Edit3, CheckCircle2, AlertCircle, ArrowRight } from "lucide-react";
import { cn } from "@/lib/utils";

interface MappingConfirmationProps {
  file: File;
  mappings: Record<string, any>;
  onConfirm: () => void;
  onEdit: () => void;
  onCancel: () => void;
  /**
   * Where this file will land (practice/ACO + program + year) — restated at
   * the last click before upload so a silent auto-selected target is never
   * invisible (owner 2026-07-14). Null when the target is chosen AFTER this
   * dialog (multi-practice TIN flow — the selection modal handles it).
   */
  targetLabel?: string | null;
  /**
   * Compact target for the CONFIRM BUTTON itself (owner 2026-07-14: the final
   * click IS the warning — you can't upload without clicking the destination).
   * e.g. "ASIMA CORP TEST (MIPS)" or "ACO A5495 (APP Plus)".
   */
  targetButton?: string | null;
  /** Tints the confirm button by program so context reads at a glance. */
  targetProgram?: 'mips' | 'app-plus' | null;
}

const TARGET_FIELDS = [
  { field: "patient_id", label: "Patient ID", required: true },
  { field: "date_of_service", label: "Date of Service", required: true },
  { field: "date_of_birth", label: "Date of Birth", required: true },
  { field: "cpt_codes", label: "CPT Codes", required: true, allowMultiple: true },
  { field: "icd10_codes", label: "ICD-10 Codes", required: true, allowMultiple: true },
  { field: "cpt_modifiers", label: "CPT Modifiers", required: false, allowMultiple: true }, // Optional - not every encounter has one
  { field: "place_of_service", label: "Place of Service", required: true },
  { field: "npi", label: "Provider NPI", required: false },
  { field: "claim_id", label: "Claim ID", required: false },
  { field: "first_name", label: "First Name", required: false },
  { field: "last_name", label: "Last Name", required: false },
  { field: "full_patient_name", label: "Full Patient Name", required: false },
  { field: "tin", label: "TIN", required: false },
  { field: "gender", label: "Gender", required: false },
];

export function MappingConfirmation({ 
  file, 
  mappings, 
  onConfirm, 
  onEdit, 
  onCancel,
  targetLabel,
  targetButton,
  targetProgram,
}: MappingConfirmationProps) {
  const [localMappings, setLocalMappings] = useState(mappings);

  // Debug logging
  console.log('MappingConfirmation - mappings received:', mappings);
  console.log('MappingConfirmation - localMappings:', localMappings);

  // Safety check for malformed mappings
  if (!localMappings || typeof localMappings !== 'object') {
    console.error('Invalid mappings data:', localMappings);
    return (
      <Dialog open={true} onOpenChange={onCancel}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Error</DialogTitle>
          </DialogHeader>
          <div className="p-4">
            <p className="text-sm text-muted-foreground mb-4">
              Invalid mapping data received. Please try mapping again.
            </p>
            <div className="flex gap-2 justify-end">
              <Button variant="outline" onClick={onCancel}>Cancel</Button>
              <Button onClick={onEdit}>Re-map Fields</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    );
  }

  // Convert mappings to display format
  // mappings structure: { targetField: sourceColumn | sourceColumns[] }
  const mappedFields = Object.entries(localMappings)
    .filter(([targetField, sourceColumns]) => sourceColumns && 
      (Array.isArray(sourceColumns) ? sourceColumns.length > 0 : sourceColumns !== ""))
    .map(([targetField, sourceColumns]) => {
      const field = TARGET_FIELDS.find(f => f.field === targetField);
      const columns = Array.isArray(sourceColumns) ? sourceColumns : [sourceColumns];
      return {
        targetField,
        targetLabel: field?.label || targetField,
        sourceColumns: columns,
        isRequired: field?.required || false,
        allowMultiple: field?.allowMultiple || false,
      };
    })
    .sort((a, b) => {
      // Sort required fields first, then alphabetically
      if (a.isRequired && !b.isRequired) return -1;
      if (!a.isRequired && b.isRequired) return 1;
      return a.targetLabel.localeCompare(b.targetLabel);
    });

  const requiredFields = TARGET_FIELDS.filter(f => f.required);
  const mappedRequiredFields = mappedFields.filter(f => f.isRequired);
  const missingRequired = requiredFields.filter(
    rf => !mappedRequiredFields.some(mf => mf.targetField === rf.field)
  );

  const handleRemoveMapping = (targetField: string) => {
    const newMappings = { ...localMappings };
    
    // Find and remove the mapping
    Object.keys(newMappings).forEach(key => {
      if (newMappings[key] === targetField) {
        delete newMappings[key];
      }
    });
    
    setLocalMappings(newMappings);
  };

  const handleConfirm = () => {
    // Update parent with any local changes
    onConfirm();
  };

  return (
    <Dialog open={true} onOpenChange={onCancel}>
      <DialogContent className="max-w-6xl max-h-[90vh] p-0 flex flex-col">
        <DialogHeader className="px-6 py-4 border-b flex-shrink-0">
          <DialogTitle className="text-lg font-semibold flex items-center gap-2">
            <CheckCircle2 className="h-5 w-5 text-green-600" />
            Confirm Field Mapping
          </DialogTitle>
          <p className="text-sm text-muted-foreground">
            Review your field mappings for {file.name} before processing
          </p>
          {targetLabel && (
            <div className="mt-1 flex items-center gap-2 rounded-md border border-primary/30 bg-primary/5 px-3 py-1.5 text-sm">
              <ArrowRight className="h-4 w-4 text-primary flex-shrink-0" />
              <span>This file will be uploaded to <span className="font-semibold">{targetLabel}</span></span>
            </div>
          )}
        </DialogHeader>

        <div className="flex-1 overflow-hidden flex flex-col">
          {/* Validation Alert */}
          {missingRequired.length > 0 && (
            <div className="px-6 pt-4">
              <Alert variant="destructive">
                <AlertCircle className="h-4 w-4" />
                <AlertDescription>
                  Missing required fields: {missingRequired.map(f => f.label).join(", ")}
                </AlertDescription>
              </Alert>
            </div>
          )}

          {/* Mapping Summary */}
          <div className="px-6 py-4 flex-1">
            <div className="grid grid-cols-2 gap-6 h-full">
              
              {/* Required Fields */}
              <div>
                <h3 className="font-semibold mb-3 flex items-center gap-2">
                  Required Fields
                  <span className="text-sm font-normal text-muted-foreground">
                    {mappedRequiredFields.length}/{requiredFields.length}
                  </span>
                </h3>
                
                <ScrollArea className="h-[450px]">
                  <div className="space-y-2">
                    {requiredFields.map(field => {
                      const mapping = mappedFields.find(m => m.targetField === field.field);
                      const isMapped = !!mapping;
                      
                      return (
                        <div
                          key={field.field}
                          className={cn(
                            "border rounded-lg p-2 transition-colors",
                            isMapped ? "bg-green-50 border-green-200" : "bg-red-50 border-red-200"
                          )}
                        >
                          <div className="flex items-center justify-between mb-1">
                            <span className="font-medium text-sm">{field.label}</span>
                            {isMapped ? (
                              <Button
                                size="sm"
                                variant="ghost"
                                className="h-6 w-6 p-0 hover:bg-red-100"
                                onClick={() => handleRemoveMapping(field.field)}
                              >
                                <X className="h-3 w-3 text-red-600" />
                              </Button>
                            ) : (
                              <Badge variant="destructive" className="text-xs">
                                Missing
                              </Badge>
                            )}
                          </div>
                          
                          {isMapped ? (
                            <div className="flex flex-wrap gap-1">
                              {mapping.sourceColumns.map(col => (
                                <Badge key={col} variant="outline" className="text-xs">
                                  {col}
                                </Badge>
                              ))}
                            </div>
                          ) : (
                            <p className="text-xs text-muted-foreground">
                              Click "Edit Mapping" to assign this field
                            </p>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </ScrollArea>
              </div>

              {/* Optional Fields */}
              <div>
                <h3 className="font-semibold mb-3">Optional Fields</h3>
                
                <ScrollArea className="h-[450px]">
                  <div className="space-y-2">
                    {mappedFields.filter(m => !m.isRequired).map(mapping => (
                      <div
                        key={mapping.targetField}
                        className="border rounded-lg p-2 bg-blue-50 border-blue-200"
                      >
                        <div className="flex items-center justify-between mb-1">
                          <span className="font-medium text-sm">{mapping.targetLabel}</span>
                          <Button
                            size="sm"
                            variant="ghost"
                            className="h-6 w-6 p-0 hover:bg-red-100"
                            onClick={() => handleRemoveMapping(mapping.targetField)}
                          >
                            <X className="h-3 w-3 text-red-600" />
                          </Button>
                        </div>
                        
                        <div className="flex flex-wrap gap-1">
                          {mapping.sourceColumns.map(col => (
                            <Badge key={col} variant="outline" className="text-xs">
                              {col}
                            </Badge>
                          ))}
                        </div>
                      </div>
                    ))}
                    
                    {mappedFields.filter(m => !m.isRequired).length === 0 && (
                      <p className="text-sm text-muted-foreground text-center py-8">
                        No optional fields mapped
                      </p>
                    )}
                  </div>
                </ScrollArea>
              </div>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t flex items-center justify-between flex-shrink-0 bg-gray-50">
          <div className="flex items-center gap-4">
            <div className="text-sm text-muted-foreground">
              {mappedFields.length} fields mapped
            </div>
            {missingRequired.length === 0 && (
              <div className="flex items-center gap-1 text-sm text-green-600">
                <CheckCircle2 className="h-4 w-4" />
                Ready to process
              </div>
            )}
          </div>
          
          <div className="flex gap-2">
            <Button variant="outline" onClick={onCancel}>
              Cancel
            </Button>
            <Button variant="outline" onClick={onEdit}>
              <Edit3 className="h-4 w-4 mr-1" />
              Edit Mapping
            </Button>
            <Button 
              onClick={handleConfirm}
              disabled={missingRequired.length > 0}
              className={cn(
                missingRequired.length > 0 && "opacity-50",
                // Program tint: APP = primary navy (default), MIPS = slate — the
                // destination reads even in peripheral vision.
                targetProgram === 'mips' && "bg-slate-700 hover:bg-slate-800 text-white",
              )}
            >
              {targetButton ? (
                <>Upload to {targetButton} <ArrowRight className="h-4 w-4 ml-1" /></>
              ) : (
                'Confirm & Process'
              )}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
